/**
 * URL and query-string helpers.
 *
 * Built on `URLSearchParams` rather than the `query-string` package. The
 * only behaviour relied on here is "serialise an object of scalars" and
 * "parse a search string", which the platform has done natively for years.
 *
 * The versions these replace came from a marketplace template and carried
 * its assumptions: `parse` accepted `latlng` and `latlngBounds` options for
 * decoding map coordinates, destructured them, and then never used them;
 * `stringify` ran values through a `serialise` helper that copied the
 * object key by key and changed nothing.
 */

/**
 * Parses a string that is *entirely* a number.
 *
 * `parseFloat` stops at the first non-numeric character, so it reads "3rd"
 * as 3. Comparing the result back against the input rejects that, which
 * matters below: a query value like "2024-01" must stay a string.
 *
 * @returns {Number|null} null when the string is not wholly a number
 */
const parseFloatNum = str => {
  const trimmed = str && typeof str.trim === 'function' ? str.trim() : null;
  if (!trimmed) return null;
  const num = parseFloat(trimmed);
  return !isNaN(num) && num.toString() === trimmed ? num : null;
};

/**
 * Serialises an object into a query string, keys sorted.
 *
 * Sorted so the same params always produce the same string — otherwise two
 * identical queries can differ by key order and read as different URLs.
 * `null` and `undefined` values are dropped rather than sent as empty.
 *
 * @param {Object} params scalars only
 * @returns {String} query string, with no leading '?'
 */
export const stringify = params => {
  const search = new URLSearchParams();
  Object.keys(params)
    .sort()
    .forEach(key => {
      const value = params[key];
      if (value !== null && typeof value !== 'undefined') {
        search.append(key, value);
      }
    });
  return search.toString();
};

/**
 * Parses a URL search query, restoring scalar types.
 *
 * 'true'/'false' come back as booleans and numeric strings as numbers, so
 * callers do not each repeat that coercion.
 *
 * @param {String} search query string, with or without a leading '?'
 * @returns {Object} key/value pairs
 */
export const parse = search => {
  const params = new URLSearchParams(
    typeof search === 'string' ? search.replace(/^[?#]/, '') : ''
  );
  const result = {};
  params.forEach((value, key) => {
    if (value === 'true') {
      result[key] = true;
    } else if (value === 'false') {
      result[key] = false;
    } else {
      const num = parseFloatNum(value);
      result[key] = num === null ? value : num;
    }
  });
  return result;
};

/**
 * Builds a Twitter/X profile URL from a handle, with or without the '@'.
 */
export const twitterPageURL = twitterHandle => {
  if (!twitterHandle) return null;
  const handle = twitterHandle.startsWith('@') ? twitterHandle.slice(1) : twitterHandle;
  return `https://twitter.com/${handle}`;
};
